/**
 * The cancel box — DESIGN-part-cancel.md (Rob, 23/10/2026). Layout A of
 * `Claude outputs/part-cancel-mock-v1.html`: "Cancel the whole booking" or "Just some of the
 * days", with the days as tick boxes.
 *
 * Opened by EVERY "Cancel this leave" button (My leave, the dashboard wallchart, a person's
 * leave page, the waiting-requests list) in place of the browser's grey "Are you sure?".
 *
 * ⚠️ THE SUMMARY IS THE SERVER'S PREVIEW (`POST /give-back` with `preview: true`) — the same
 * plan the real change follows — never worked out here. `lib/cancelLeave.js` only picks the
 * words around it.
 *
 * Props: `requestId` (null = closed), `onClose()`, `onDone()` after anything changed.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { leaveApi } from '../lib/api';
import {
  REST, SOME, WHOLE, actionLabel, firstChoice, statusLine, toldSentence, whoseBalance, whoseBooking,
} from '../lib/cancelLeave';

function Choice({ selected, onSelect, title, detail, children, testid }) {
  return (
    <div
      className={`rounded-lg border p-3 transition-colors ${
        selected ? 'border-amber-500 bg-amber-50/60 dark:bg-amber-500/10' : 'border-border hover:bg-secondary/40'}`}
    >
      <button type="button" onClick={onSelect} data-testid={testid} className="w-full text-left flex gap-3">
        <span className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
          selected ? 'border-amber-500' : 'border-muted-foreground/50'}`}>
          {selected && <span className="w-2 h-2 rounded-full bg-amber-500" />}
        </span>
        <span>
          <span className="block font-medium text-sm">{title}</span>
          {detail && <span className="block text-xs text-muted-foreground mt-0.5">{detail}</span>}
        </span>
      </button>
      {children}
    </div>
  );
}

export default function CancelLeaveDialog({ requestId, onClose, onDone }) {
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [choice, setChoice] = useState(null);
  const [ticked, setTicked] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [busy, setBusy] = useState(false);

  // A fresh box every time it opens on a booking.
  useEffect(() => {
    setInfo(null); setLoadError(''); setChoice(null); setTicked([]);
    setPreview(null); setPreviewError(''); setBusy(false);
    if (!requestId) return undefined;
    let live = true;
    leaveApi.getCancelDays(requestId)
      .then((r) => { if (live) setInfo(r.data); })
      .catch((e) => { if (live) setLoadError(e.response?.data?.detail || 'Could not load this booking.'); });
    return () => { live = false; };
  }, [requestId]);

  const first = useMemo(() => (info ? firstChoice(info) : null), [info]);

  // Which days the chosen option gives back.
  const chosenDays = useMemo(() => {
    if (!info) return [];
    if (choice === WHOLE || choice === REST) return first ? first.days : [];
    if (choice === SOME) return ticked;
    return [];
  }, [info, choice, ticked, first]);

  // The server's preview of exactly that.
  useEffect(() => {
    setPreview(null); setPreviewError('');
    if (!requestId || !info || !info.can_choose_days || chosenDays.length === 0) return undefined;
    let live = true;
    const t = setTimeout(() => {
      leaveApi.giveBack(requestId, chosenDays, true)
        .then((r) => { if (live) setPreview(r.data); })
        .catch((e) => { if (live) setPreviewError(e.response?.data?.detail || 'Could not work that out.'); });
    }, 150);
    return () => { live = false; clearTimeout(t); };
  }, [requestId, info, chosenDays]);

  const toggle = (date) => {
    setChoice(SOME);
    setTicked((was) => (was.includes(date) ? was.filter((d) => d !== date) : [...was, date].sort()));
  };

  const finish = useCallback(async () => {
    setBusy(true);
    try {
      if (!info.can_choose_days || choice === WHOLE) {
        await leaveApi.cancelRequest(requestId);
        toast.success('Leave cancelled');
      } else {
        const r = await leaveApi.giveBack(requestId, chosenDays, false);
        toast.success(r.data?.whole ? 'Leave cancelled'
          : `${r.data?.given_back_length || 'Days'} given back`);
      }
      onDone && onDone();
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel this leave');
      setBusy(false);
    }
  }, [info, choice, requestId, chosenDays, onDone, onClose]);

  const open = !!requestId;
  const what = info ? `${info.leave_type} · ${info.dates} · ${info.length}` : '';
  const isOwn = !info || info.is_own;
  const keepLabel = isOwn ? 'Keep my booking' : 'Keep it';

  // ── One day, or part of a day: nothing to choose ─────────────────────────────────
  const simple = info && !info.can_choose_days;
  const nothingLeft = info && first && first.kind === null;

  let body;
  if (loadError) {
    body = <p className="text-sm text-red-600">{loadError}</p>;
  } else if (!info) {
    body = <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  } else if (nothingLeft) {
    body = <p className="text-sm" data-testid="cancel-nothing-left">{first.detail}</p>;
  } else if (simple) {
    const told = toldSentence(info.told_if_whole);
    body = (
      <p className="text-sm">
        {info.status === 'pending' ? 'The request will be withdrawn.'
          : (info.deducted ? `The time goes back on ${whoseBalance(info)}.` : 'It will be taken off.')}
        {told ? ` ${told}` : ''}
      </p>
    );
  } else {
    body = (
      <div className="space-y-2">
        <Choice
          selected={choice === first.kind}
          onSelect={() => setChoice(first.kind)}
          title={first.title}
          detail={first.detail}
          testid="cancel-choice-whole"
        />
        <Choice
          selected={choice === SOME}
          onSelect={() => setChoice(SOME)}
          title="Just some of the days"
          detail="Tick the days you no longer want off."
          testid="cancel-choice-some"
        >
          {choice === SOME && (
            <div className="mt-2 ml-7 space-y-0.5" data-testid="cancel-days">
              {info.days.map((d) => {
                const on = ticked.includes(d.date);
                return (
                  <label
                    key={d.date}
                    className={`flex items-center gap-2.5 py-1 text-sm ${d.blocked ? 'text-muted-foreground' : 'cursor-pointer'}`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-red-600"
                      checked={on}
                      disabled={!!d.blocked}
                      onChange={() => toggle(d.date)}
                      data-testid={`cancel-day-${d.date}`}
                    />
                    <span className={on ? 'line-through text-muted-foreground' : ''}>
                      {d.label}{d.today ? ' (today)' : ''}
                    </span>
                    {d.blocked && (
                      <span className="text-[11px] rounded-full px-2 py-0.5 bg-secondary text-muted-foreground">{d.blocked}</span>
                    )}
                    {!d.blocked && d.past && (
                      <span className="text-[11px] rounded-full px-2 py-0.5 bg-secondary text-muted-foreground">In the past</span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </Choice>

        {preview && !preview.whole && (
          <div className="rounded-lg bg-secondary/60 px-3 py-2.5 text-sm space-y-1" data-testid="cancel-summary">
            {preview.parts.length === 1 ? (
              <p><span className="text-muted-foreground">{whoseBooking(info)} becomes</span> <b>{preview.parts[0]}</b></p>
            ) : (
              <>
                <p className="text-muted-foreground">{whoseBooking(info)} becomes {preview.parts.length === 2 ? 'two' : preview.parts.length}:</p>
                {preview.parts.map((p) => <p key={p}><b>{p}</b></p>)}
              </>
            )}
            {info.deducted && preview.given_back_length && (
              <p>
                <span className="text-muted-foreground">
                  {info.status === 'pending' ? 'No longer held back' : `Back on ${whoseBalance(info)}`}
                </span>{' '}
                <b>{preview.given_back_length}</b>
              </p>
            )}
            <p className="text-muted-foreground">
              {statusLine(info, preview)}{toldSentence(info.told_if_part) ? ` ${toldSentence(info.told_if_part)}` : ''}
            </p>
          </div>
        )}
        {preview && preview.whole && choice === SOME && (
          <div className="rounded-lg bg-secondary/60 px-3 py-2.5 text-sm" data-testid="cancel-summary">
            <p>That's every day, so the whole booking will be cancelled.{toldSentence(info.told_if_whole) ? ` ${toldSentence(info.told_if_whole)}` : ''}</p>
          </div>
        )}
        {choice === WHOLE && !preview && toldSentence(info.told_if_whole) && (
          <p className="text-xs text-muted-foreground px-1">{toldSentence(info.told_if_whole)}</p>
        )}
        {previewError && <p className="text-sm text-red-600">{previewError}</p>}

        <p className="text-xs text-muted-foreground pt-1">
          Whole days only. To turn a day into a half day, give the day back and book the half day.
        </p>
      </div>
    );
  }

  const canAct = info && !nothingLeft && !busy && !loadError
    && (simple || (choice && chosenDays.length > 0 && !previewError));
  const label = simple ? 'Cancel this leave' : actionLabel({ choice, preview, ticked });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose && onClose(); }}>
      <DialogContent className="sm:max-w-[460px]" data-testid="cancel-leave-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">
            {simple ? 'Cancel this leave?' : 'Cancel this leave'}
          </DialogTitle>
          <DialogDescription>
            {info && !isOwn ? `${info.employee_name} · ` : ''}{what}
          </DialogDescription>
        </DialogHeader>
        {body}
        <div className="flex justify-end gap-2 pt-2 flex-wrap">
          <Button variant="outline" onClick={onClose} disabled={busy}>{nothingLeft ? 'Close' : keepLabel}</Button>
          {!nothingLeft && !loadError && (
            <Button
              variant="destructive"
              onClick={finish}
              disabled={!canAct}
              data-testid="cancel-leave-confirm"
            >
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {label}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
